import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../services/firebase';

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Add to Firebase Firestore
      await addDoc(collection(db, 'contacts'), {
        ...formData,
        createdAt: new Date()
      });

      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });

      setTimeout(() => {
        setSuccess(false);
      }, 5000);
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Page Header */}
      <div className="relative bg-primary py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold text-white mb-4 animate-fadeInDown">Contact Us</h1>
          <nav className="flex justify-center animate-fadeInUp">
            <ol className="flex items-center space-x-2 text-white">
              <li><Link to="/" className="hover:text-secondary">Home</Link></li>
              <li><i className="fas fa-chevron-right mx-2"></i></li>
              <li className="text-secondary">Contact</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Contact Info Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 animate-fadeInUp">
            {/* <h5 className="text-secondary font-semibold mb-2">// Contact Us</h5> */}
            <h1 className="text-4xl md:text-5xl font-bold">Get In Touch With Us</h1>
          </div>

          <div className="flex flex-col md:flex-row justify-center gap-12 md:gap-24 mb-16">
            {/* Address */}
            {/* <div className="text-center animate-fadeInUp">
              <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-map-marker-alt text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-2">Our Office</h4>
              <p className="text-gray-600">26/6 Paris Town Batala Road, Amritsar</p>
            </div> */}

            {/* Phone */}
            <div className="text-center animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
              <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-phone-alt text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-2">Call Us</h4>
              <p className="text-gray-600">+91 8146883319</p>
            </div>

            {/* Email */}
            <div className="text-center animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
              <div className="bg-primary w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-envelope text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold mb-2">Mail Us</h4>
              <p className="text-gray-600">support@nexedz.org</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="animate-fadeInLeft">
              <h3 className="text-3xl font-bold mb-6">Send Us A Message</h3>
              
              {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 animate-fadeIn">
                  <i className="fas fa-check-circle mr-2"></i>
                  Your message has been sent successfully!
                </div>
              )}

              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 animate-fadeIn">
                  <i className="fas fa-exclamation-circle mr-2"></i>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your Name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="Your Email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="Your Phone"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-gray-600"
                    >
                      <option value="">Select A Service</option>
                      <option value="Web Development">Web Development</option>
                      <option value="Digital Marketing">Digital Marketing</option>
                      <option value="Skill Academy">Skill Academy</option>
                      <option value="Bharat Ki Digital Dukan">Bharat Ki Digital Dukan</option>
                      <option value="Workforce Solution">Workforce Solution</option>
                      <option value="Support">24/7 Support</option>
                    </select>
                  </div>
                </div>

                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Message"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-secondary text-white font-semibold py-4 rounded-lg transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="fas fa-paper-plane ml-2"></i>
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Map */}
            <div className="animate-fadeInRight">
              <h3 className="text-3xl font-bold mb-6">Find Us On Map</h3>
              <div className="rounded-lg overflow-hidden shadow-lg h-full min-h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3399.7447876757934!2d74.89583931511767!3d31.646099581324707!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39196338973193f3%3A0xce99999ab7d44222!2s26A%2C%20Paris%20Town%2C%20Amritsar%2C%20Punjab%20143001!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Google Maps"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0 animate-fadeInLeft">
              <h3 className="text-3xl font-bold text-white mb-2">Subscribe Our Newsletter</h3>
              <p className="text-gray-200">Stay updated with our latest news and offers</p>
            </div>
            <div className="w-full md:w-auto animate-fadeInRight">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Your email address"
                  className="px-6 py-4 rounded-lg focus:outline-none w-full sm:w-80"
                />
                <button className="bg-secondary hover:bg-white hover:text-primary text-white font-semibold px-8 py-4 rounded-lg transition whitespace-nowrap">
                  Subscribe Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;